import {createSelector} from 'reselect';

import {isElectron} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {defaultIntegrationFrameIds, nonEmailChannelTypes} from '../../components/startGuide/startGuideCommon';
import {isDemoInbox} from '../../helpers/modelHelpers';
import {OnboardingTipTypesEnum, StartGuideTaskTypesEnum} from '../../models/api/preferencesModel';
import {getFlatAnswers} from '../data/answers/answersSelectors';
import {getUserParticipatingChannels} from '../data/channels/channelsSelectors';
import {getEnabledIntegrations} from '../data/dataSelectors';
import {getUserOwnedInboxes, getUserTeamParticipatingInboxes} from '../data/inboxes/inboxesSelectors';
import {getCompletedStartGuideTasks, getSeenOnboardingTips} from '../data/preferences/preferencesSelectors';
import {getFlatActiveOrPendingTeammates, getIsUserGlobalAdmin} from '../data/teammates/teammatesSelectors';
import {
  getHasNonGlobalAdminStartGuideFlag,
  getHasStartGuideFlag,
} from '../featureFlags/featureFlagsSelectors';

/*
 * Selectors.
 */

export const getShouldAddTeamInbox = createSelector(
  getUserTeamParticipatingInboxes,
  // Must have at least one inbox that is not the demo inbox.
  (teamParticipatingInboxes) => teamParticipatingInboxes.filter((i) => !isDemoInbox(i)).length < 1,
);

export const getShouldAddIndividualInbox = createSelector(
  getUserOwnedInboxes,
  (inboxes) => inboxes.filter((i) => !isDemoInbox(i)).length < 1,
);

export const getShouldAddTeammate = createSelector(
  getFlatActiveOrPendingTeammates,
  (teammates) => teammates.filter((t) => !t.isGuidedDemo).length < 2, // Filter out the demo teammate.
);

export const getShouldAddAnswer = createSelector(
  getFlatAnswers,
  // Check if answer was created by onboarding. If not, then assume it was
  // created by the user.
  (answers) => {
    const userCreatedAnswers = answers.filter((answer) => !answer.wasCreatedByOnboarding);

    return userCreatedAnswers.length < 1;
  },
);

export const getShouldAddNonEmailChannel = createSelector(
  getUserParticipatingChannels,
  // If every team channel the user has is an email channel type then
  // addNonEmailChannelTask is incomplete.
  (userParticipatingChannels) =>
    userParticipatingChannels.every((channel) => !nonEmailChannelTypes.includes(channel.type)),
);

export const getShouldAddNonDefaultIntegration = createSelector(
  getEnabledIntegrations,
  // If every enabled integration is either Calendar or Contacts, then
  // connectIntegrationTask is incomplete.
  (enabledIntegrations) =>
    enabledIntegrations.every((integration) => defaultIntegrationFrameIds.includes(integration.frameId)),
);

export const getHasGlobalAdminStartGuide = createSelector(
  [getIsUserGlobalAdmin, getHasStartGuideFlag],
  // User must be global admin and have startGuide feature flag.
  (isUserGlobalAdmin, hasStartGuide) => isUserGlobalAdmin && hasStartGuide,
);

export const getHasNonGlobalAdminStartGuide = createSelector(
  [getIsUserGlobalAdmin, getHasNonGlobalAdminStartGuideFlag],
  // User must be not be a global admin and have nonGlobalAdminStartGuide feature flag.
  (isUserGlobalAdmin, hasNonGlobalAdminStartGuideFlag) =>
    !isUserGlobalAdmin && hasNonGlobalAdminStartGuideFlag,
);

export const getHasStartGuide = createSelector(
  [getHasGlobalAdminStartGuide, getHasNonGlobalAdminStartGuide],
  (hasGlobalAdminStartGuide, hasNonGlobalAdminStartGuide) =>
    hasGlobalAdminStartGuide || hasNonGlobalAdminStartGuide,
);

const getAreCommonStartGuideTasksIncomplete = createSelector(
  // The common startguide tasks between the globalAdmin and nonGlobalAdmin
  // start guides are 1) adding a team inbox, 2) adding an individual inbox, and
  // 3) using Electron and/or clicking the download app link.
  [getShouldAddTeamInbox, getShouldAddIndividualInbox, getCompletedStartGuideTasks],
  (shouldAddTeamInbox, shouldAddIndividualInbox, completedStartGuideTasks) =>
    shouldAddTeamInbox ||
    shouldAddIndividualInbox ||
    (!isElectron() && !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.DOWNLOAD_APP)),
);

export const getShouldExploreSettings = createSelector(
  [getCompletedStartGuideTasks],
  (completedStartGuideTasks) =>
    // For legacy reasons, we are using the team rules task to determine whether the user
    // should explore settings.
    !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.ADD_TEAM_RULES),
);

export const getIsStartGuideIncomplete = createSelector(
  // Controls automatically opening start guide and displaying incomplete badge
  // next to start guide icon in P4.
  [
    getAreCommonStartGuideTasksIncomplete,
    getShouldExploreSettings,
    getShouldAddTeammate,
    getShouldAddAnswer,
    getCompletedStartGuideTasks,
    getHasGlobalAdminStartGuide,
  ],
  (
    areCommonStartGuideTasksIncomplete,
    shouldExploreSettings,
    shouldAddTeammate,
    shouldAddAnswer,
    completedStartGuideTasks,
    hasGlobalAdminStartGuide,
  ) => {
    // Check if common tasks for all startGuides are incomplete.
    if (areCommonStartGuideTasksIncomplete) {
      return true;
    }

    if (hasGlobalAdminStartGuide) {
      return shouldExploreSettings || shouldAddTeammate;
    }

    // If the common tasks are complete, check 1) sending an email and
    // 2) mentioning a teammate for nonGlobalAdmin start guide.
    return (
      !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.SEND_EMAIL) ||
      !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.MENTION_TEAMMATE)
    );
  },
);

export const getHasExploredDemoInbox = createSelector(
  [getSeenOnboardingTips],
  (seenOnboardingTips) =>
    seenOnboardingTips.includes(OnboardingTipTypesEnum.DEMO_INBOX_COMMENT) &&
    seenOnboardingTips.includes(OnboardingTipTypesEnum.DEMO_INBOX_COMPOSER) &&
    seenOnboardingTips.includes(OnboardingTipTypesEnum.DEMO_INBOX_TEAM_WORKSPACE) &&
    seenOnboardingTips.includes(OnboardingTipTypesEnum.DEMO_INBOX_RULE),
);
